import { CommandController, ICommandDispatcher } from '@Common/ApplicationCore/CommandController';
import { about, IBaseDocumentTreeSetting } from '@Common/Controls';
import { treeDocuments } from '@Common/Controls/Documents/treeDocuments';
import constants from '@Common/Core/Constant';
import { DataServiceProvider } from '@Common/Core/DataServiceProvider';
import { MulticastDelegate } from '@Common/Core/MulticastDelegate';
import { ApplicationType } from '@Common/Enums';
import urlHelper from '@Common/Helpers/UrlHelper';
import { BaseModel } from '@InstanceFactory';
import { ChangePasswordDialog } from '@View/Dialogs/ChangePasswordDialog';

import AppHelper from './AppHelper';
import { ApplicationLayout, IApplicationLayoutParam } from './ApplicationLayout';
import { Command } from './Command';
import DocumentsTreeService from './DocumentsTreeService';
import { enumerable } from '@Common/Core/CoreDecorator';
import { ServerError } from '@Common/Helpers/Errors';
import errorHelper from '@Common/Helpers/ErrorHelper';

export type ApplicationBase = Application<BaseModel>;
export type BaseTreePanel = treeDocuments.CommonPanel<treeDocuments.CommonView<treeDocuments.CommonStore>, treeDocuments.CommonStore>;
export type ApplicationHelp = { tooltip: string; location: string };

export default abstract class Application<TModel extends BaseModel, TApplicationLayout extends ApplicationLayout = ApplicationLayout>
    implements ICommandDispatcher {
    private static instance: ApplicationBase;
    private static supportCommands = [constants.commands.recentItems, constants.commands.about, constants.commands.logout, constants.commands.help];
    public onLoadComplete = MulticastDelegate.create<IHandler>();
    protected root = $('body');
    public readonly commandController = new CommandController(this.root);
    protected layout: TApplicationLayout;
    protected dataServiceProvider = new DataServiceProvider();
    protected documentsTreeService = new DocumentsTreeService();
    private autosaveIntervalId: number;

    protected constructor() {
        Application.instance = this;
        this.commandController.addDispatcher(this);
        this.startAutosaveTimer();
        window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
            if (!altosoft.isDebug) {
                e.preventDefault();
            }

            if (e.reason instanceof ServerError) {
                const { error, message } = e.reason;
                errorHelper.showException(message, error).shutdown();
                e.preventDefault();
            }
        });
    }

    @enumerable
    public static get current(): ApplicationBase {
        return Application.instance;
    }

    @enumerable
    public static get model(): BaseModel {
        return altosoft.model;
    }

    public abstract get id(): ApplicationType;

    @enumerable
    public get model(): TModel {
        return Application.model as TModel;
    }

    public abstract start(): void;

    public abstract getHelp(): ApplicationHelp;

    public getSettings(): IBaseDocumentTreeSetting {
        return {
            commandController: this.commandController,
            dataServiceProvider: this.dataServiceProvider,
            get documentTabs() {
                return Application.current.layout.getDocumentTabs();
            },
            projectId: AppHelper.projectId,
            // TODO (MAS) WTF
            insightSettings: altosoft.model ? altosoft.model.data : null,
            insightAuthenticationData: altosoft.model ? altosoft.model.data : null
        };
    }

    public getDocumentTabs() {
        return this.layout.getDocumentTabs();
    }

    public reload(): void {
        window.location.reload(true);
    }

    public getCommandValue(commandId: string): boolean | undefined {
        switch (commandId) {
            case constants.commands.toggleTestSession:
                return this.model.user.isTestProjectSessionStarted;
        }
    }

    public isDisabledCommand(command: string): boolean {
        return false;
    }

    public isSupportCommands(category: string, command: string): boolean {
        const model = this.model;
        return (
            Application.supportCommands.contains(command) || (command === constants.commands.changePassword && model && model.user.canChangePassword)
        );
    }

    public processCommand(command: Command): boolean {
        switch (command.id) {
            case constants.commands.saveAll:
                for (const editor of this.getDocumentTabs()
                    .getEditors()
                    .map(se => se.editor)) {
                    editor.processCommand(command);
                }
                return true;
            case constants.commands.help:
                urlHelper.openHelp(this.getHelp().location, this.getContextHelpId());
                return true;
            case constants.commands.logout:
                urlHelper.logout();
                return true;
            case constants.commands.about:
                about();
                return true;
            case constants.commands.changePassword:
                new ChangePasswordDialog(this.dataServiceProvider).open();
                return true;
            default:
                return false;
        }
    }

    public addRecentItem(item): void {
        this.layout.addRecentItem(item);
    }

    protected abstract createLayout(param: IApplicationLayoutParam): TApplicationLayout;

    protected startAutosaveTimer(): void {
        if (this.autosaveIntervalId) {
            clearInterval(this.autosaveIntervalId);
            delete this.autosaveIntervalId;
        }
        const studioSettings = AppHelper.cookieStudioSettings();
        if (studioSettings.autosave && studioSettings.autosavePeriod > 0) {
            this.autosaveIntervalId = setInterval(() => {
                const saveAllCommand = this.commandController.getCommand(constants.commands.saveAll);
                this.processCommand(saveAllCommand);
            }, studioSettings.autosavePeriod);
        }
    }

    private getContextHelpId(): string {
        return this.layout?.getDocumentTabs()?.getActiveEditor()?.editor?.getContextHelpId() ?? null;
    }
}
